import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { publicApiClient } from '@/lib/api/publicClient';

import { sessionDetailDtoSchema } from '../schemas/sessionSchemas';

// Mobile-number verification on a workflow's common link (/wf/{slug}).
//
// Two anonymous calls: request a code, then verify it. Verification is what
// creates or resumes the session — nothing is written before that — so its
// response carries the session to run plus WHY it's in that state.

/** What the backend decided about this number's run of the workflow. */
export const SESSION_ENTRY_OUTCOME = {
  /** No previous run — a fresh session was created. */
  Started: 'started',
  /** An in-progress run is being continued where it left off. */
  Resumed: 'resumed',
  /** This number has already been through the workflow; nothing new was created. */
  AlreadyUsed: 'already-used',
} as const;

const sessionEntryResultSchema = z.object({
  outcome: z.string(),
  session: sessionDetailDtoSchema,
});

export type SessionEntryResult = z.infer<typeof sessionEntryResultSchema>;

/** Sends a 6-digit code to the applicant's mobile number. */
export function useRequestSessionOtp(slug: string | undefined, refId?: string) {
  return useMutation({
    mutationFn: async (mobileNumber: string) => {
      await publicApiClient.post(`api/v1/public/workflows/${slug}/otp/request`, {
        json: { mobileNumber },
        searchParams: refId ? { ref: refId } : undefined,
      });
    },
  });
}

/**
 * Verifies the code and returns the session to run.
 *
 * Never retried: this call CONSUMES the code and creates the session, so a retry
 * after a network blip would present an already-spent code and read as a failed
 * attempt. `retry: 0` overrides publicApiClient's default of one retry, and
 * react-query's own retry is off too.
 */
export function useVerifySessionOtp(
  slug: string | undefined,
  refId?: string,
  inviteToken?: string,
) {
  return useMutation({
    retry: false,
    mutationFn: async ({ mobileNumber, code }: { mobileNumber: string; code: string }) => {
      const raw = await publicApiClient
        .post(`api/v1/public/workflows/${slug}/otp/verify`, {
          // inviteToken: attribution only — tells the server which emailed invite
          // this visit came from, so "sent but not started" can be answered.
          json: { mobileNumber, code, inviteToken: inviteToken ?? null },
          searchParams: refId ? { ref: refId } : undefined,
          retry: 0,
        })
        .json();
      return sessionEntryResultSchema.parse(raw);
    },
  });
}
